import { useTranslation } from 'react-i18next';
import {
  SubformComponent,
  SubCardFieldsComponent,
  SubModule,
} from '@/modules/admin/audits/components/submodules/types';
import { LineText } from '@common/components/fragments/LineText';
import AuditsExportsActiveEmployeesExport from '@/app/spraypaint/audits/exports/AuditsExportsActiveEmployeesExport';
import { ControlledDateInput } from '@common/components/inputs/controlledInput/ControlledDateInput';
import { ControlledSelect } from '@common/components/inputs/controlledInput/ControlledSelect';

export const jsonApiType = AuditsExportsActiveEmployeesExport.jsonapiType;

export type Values = {
  date: AuditsExportsActiveEmployeesExport['date'];
  employeeSelection: AuditsExportsActiveEmployeesExport['employeeSelection'];
};

export const defaultValues: Values = {
  // Today, iso8601 format date
  date: new Date().toISOString().split('T')[0],
  employeeSelection: 'with_contract',
};

function createExportStrategy(
  data: Values,
): AuditsExportsActiveEmployeesExport {
  return new AuditsExportsActiveEmployeesExport({
    date: data.date,
    employeeSelection: data.employeeSelection,
  });
}

export const FormComponent: SubformComponent = ({ control }) => {
  const { t } = useTranslation();

  const employeeSelectionOptions = ['with_contract', 'not_archived'].map(
    (key) => ({
      value: key,
      label: t(
        [
          'activerecord.enums.audits/exports/active_employees_export.employee_selection',
          key,
        ].join('.'),
        {
          ns: 'rails',
        },
      ),
    }),
  );

  return (
    <>
      <ControlledDateInput
        label={t(
          'activerecord.attributes.audits/exports/active_employees_export.date',
          {
            ns: 'rails',
          },
        )}
        className="mt-20"
        control={control}
        defaultValue={undefined}
        name="exportStrategy.params.date"
        stringMode
        required
      />{' '}
      <ControlledSelect
        label={t(
          'activerecord.attributes.audits/exports/active_employees_export.employee_selection',
          {
            ns: 'rails',
          },
        )}
        className="mt-20"
        control={control}
        name="exportStrategy.params.employeeSelection"
        options={employeeSelectionOptions}
        required
      />
    </>
  );
};

export const CardFieldsComponent: SubCardFieldsComponent<
  AuditsExportsActiveEmployeesExport
> = ({ strategy }) => {
  const { t } = useTranslation();

  return (
    <>
      <LineText
        label={t('activerecord.attributes.audits/export.export_strategy', {
          ns: 'rails',
        })}
        value={t(
          'activerecord.models.audits/exports/active_employees_exports',
          {
            ns: 'rails',
          },
        )}
      />
      <LineText
        label={t(
          'activerecord.attributes.audits/exports/active_employees_export.date',
          {
            ns: 'rails',
          },
        )}
        value={strategy.date}
      />
      <LineText
        label={t(
          'activerecord.attributes.audits/exports/active_employees_export.employee_selection',
          {
            ns: 'rails',
          },
        )}
        value={t(
          [
            'activerecord.enums.audits/exports/active_employees_export.employee_selection',
            strategy.employeeSelection,
          ].join('.'),
          {
            ns: 'rails',
          },
        )}
      />
    </>
  );
};

export const AuditsExportsActiveEmployeesExportSubmodule: SubModule<
  Values,
  AuditsExportsActiveEmployeesExport
> = {
  jsonApiType,
  defaultValues,
  FormComponent,
  CardFieldsComponent,
  createExportStrategy,
};
